import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLiveShowContext } from '../contexts/LiveShowContext';
import { useGlobalLiveShowChange } from '../contexts/GlobalLiveShowChangeContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useScheduleState } from '../hooks/useScheduleState';
import { useShowAnimation } from '../hooks/ui/useShowAnimation';
import { renderSubTitleIfValid } from '../utils/subTitleUtils';
import { LoadingDots } from './ui/LoadingStates';
import WhatsAppContact from './WhatsAppContact';
import StoreErrorBoundary from './common/StoreErrorBoundary';
import { Show } from '../types/Show';
import { debugLog, debugVerbose } from '../utils/debugLog';
import OptimizedShowImage from './common/OptimizedShowImage';
import { DEFAULT_SHOW_FALLBACK_IMAGE } from '../constants/images';

interface LiveShowHeroProps {
  className?: string;
}

const HERO_MIN_HEIGHT = 440;
const DEFAULT_SHOW_IMAGE = DEFAULT_SHOW_FALLBACK_IMAGE;

const sanitizeText = (value?: string | null, fallback?: string): string | undefined => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  const normalized = trimmed.toLowerCase();
  if (['null', 'undefined', 'n/a', 'na', 'none'].includes(normalized)) {
    return fallback;
  }

  return trimmed;
};

const sanitizeImageCandidate = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const normalized = trimmed.toLowerCase();
  if (normalized === 'null' || normalized === 'undefined') {
    return undefined;
  }
  if (trimmed.endsWith('/')) {
    return undefined;
  }
  return trimmed;
};

const mergeShowImages = (
  incoming: Show | null | undefined,
  fallback: Show | null | undefined
): Record<string, string | undefined> => {
  const priorities = ['600x600', '500x500', '400x400', '300x300', '150x150'];
  const merged: Record<string, string | undefined> = {};

  priorities.forEach((key) => {
    const candidate =
      sanitizeImageCandidate(incoming?.images?.[key]) ??
      sanitizeImageCandidate(fallback?.images?.[key]);
    if (candidate) {
      merged[key] = candidate;
    }
  });

  if (!merged['300x300']) {
    merged['300x300'] =
      sanitizeImageCandidate(fallback?.images?.['300x300']) ?? DEFAULT_SHOW_IMAGE;
  }

  return merged;
};

const normalizeShowForDisplay = (
  incoming: Show | null | undefined,
  fallback: Show | null | undefined = null
): Show | null => {
  if (!incoming && !fallback) {
    return null;
  }

  const base = incoming ?? fallback!;
  const presenterName =
    sanitizeText(base.presenter?.name, fallback?.presenter?.name ?? 'Eruption Radio') ??
    'Eruption Radio';
  const presenterGenres =
    sanitizeText(base.presenter?.genres, fallback?.presenter?.genres ?? 'Various') ?? 'Various';

  return {
    ...fallback,
    ...base,
    show:
      sanitizeText(base.show, fallback?.show ?? 'Live Show') ??
      fallback?.show ??
      'Live Show',
    presenter: {
      ...fallback?.presenter,
      ...base.presenter,
      name: presenterName,
      genres: presenterGenres,
    },
    images: mergeShowImages(base, fallback),
    sub_title: sanitizeText(base.sub_title),
  };
};

const areShowsEquivalent = (a: Show | null, b: Show | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.show_id === b.show_id &&
    a.date_start_time === b.date_start_time &&
    a.show === b.show
  );
};

const LiveShowHeroContent: React.FC<LiveShowHeroProps> = ({ className = '' }) => {
  // Use centralized live show context instead of direct query
  const {
    currentLiveShow: liveShow,
    loading,
    error,
    refetchLiveShow
  } = useLiveShowContext();
  const { playLiveStream } = useAudioPlayer();
  
  // Use global live show change detection
  const { onLiveShowChange, onNewPastShowAvailable } = useGlobalLiveShowChange();
  
  // Get schedule refresh function for immediate updates
  const { refreshSchedule } = useScheduleState();
  
  // Use show animation hook for homepage animations
  const { triggerAnimation } = useShowAnimation({
    animationDuration: 2000,
    enableLogging: true
  });
  
  // State for smooth transitions
  const [displayShow, setDisplayShow] = useState<Show | null>(() =>
    normalizeShowForDisplay(liveShow)
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const lastDisplayShowRef = useRef<Show | null>(displayShow);
  const clearDisplayTimeoutRef = useRef<number | null>(null);

  // Ensure the live hero reflects the latest live show when navigating back to Home
  // (e.g. when a tab has been open for a long time).
  useEffect(() => {
    refreshSchedule();
    void refetchLiveShow();
  }, [refreshSchedule, refetchLiveShow]);

  // Function to handle live show changes (now triggered by global context)
  const handleLiveShowChange = useCallback((newShow: Show | null, previousShow: Show | null) => {
    debugVerbose('🔄 LiveShowHero: Live show change detected via global context');
    debugVerbose('🔄 Previous:', previousShow?.show || 'None');
    debugVerbose('🔄 New:', newShow?.show || 'None');
    
    // Immediately refresh schedule data (feeds Up Next section)
    refreshSchedule();

    // Force a live show refetch to pick up latest metadata quickly
    void refetchLiveShow();

    if (newShow) {
      setIsTransitioning(true);
      const sanitized = normalizeShowForDisplay(newShow, lastDisplayShowRef.current);
      setDisplayShow(sanitized);
      lastDisplayShowRef.current = sanitized;
      window.setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [refreshSchedule, refetchLiveShow]);
  
  // Function to handle new past show available (for homepage animation)
  const handleNewPastShowAvailable = useCallback((newShows: Show[]) => {
    debugVerbose('🎉 LiveShowHero: New past show(s) available for homepage animation:',
      newShows.map(s => s.show));
    
    // Trigger animation for the newest show (first in array)
    if (newShows.length > 0) {
      const newestShow = newShows[0];
      debugVerbose('🎬 LiveShowHero: Triggering animation for newest show:', newestShow.show);
      triggerAnimation(newestShow);
    }
  }, [triggerAnimation]);

  // Subscribe to global live show changes
  useEffect(() => {
    debugVerbose('🔄 LiveShowHero: Subscribing to global live show changes');
    const unsubscribeLiveShow = onLiveShowChange(handleLiveShowChange);
    
    return () => {
      debugVerbose('🔄 LiveShowHero: Unsubscribing from global live show changes');
      unsubscribeLiveShow();
    };
  }, [onLiveShowChange, handleLiveShowChange]);
  
  // Subscribe to global new past show events
  useEffect(() => {
    debugVerbose('🎉 LiveShowHero: Subscribing to global new past show events');
    const unsubscribeNewPastShow = onNewPastShowAvailable(handleNewPastShowAvailable);
    
    return () => {
      debugVerbose('🎉 LiveShowHero: Unsubscribing from global new past show events');
      unsubscribeNewPastShow();
    };
  }, [onNewPastShowAvailable, handleNewPastShowAvailable]);

  // Handle smooth transitions when live show changes
  useEffect(() => {
    if (liveShow) {
      if (clearDisplayTimeoutRef.current) {
        clearTimeout(clearDisplayTimeoutRef.current);
        clearDisplayTimeoutRef.current = null;
      }

      const sanitized = normalizeShowForDisplay(liveShow, lastDisplayShowRef.current);
      if (!areShowsEquivalent(sanitized, lastDisplayShowRef.current)) {
        setIsTransitioning(true);
        setDisplayShow(sanitized);
        lastDisplayShowRef.current = sanitized;
        window.setTimeout(() => setIsTransitioning(false), 300);
      }
    } else if (displayShow) {
      if (!clearDisplayTimeoutRef.current) {
        clearDisplayTimeoutRef.current = window.setTimeout(() => {
          debugLog('ℹ️ Live show offline for extended period, clearing hero display');
          setDisplayShow(null);
          lastDisplayShowRef.current = null;
          clearDisplayTimeoutRef.current = null;
        }, 60 * 1000);
      }
    } else {
      lastDisplayShowRef.current = null;
    }

    return () => {
      if (clearDisplayTimeoutRef.current && liveShow) {
        clearTimeout(clearDisplayTimeoutRef.current);
        clearDisplayTimeoutRef.current = null;
      }
    };
  }, [liveShow, displayShow]);

  // Show loading state
  if (loading) {
    return (
      <div className={`w-full mb-8 sm:mb-12 ${className}`} style={{ minHeight: HERO_MIN_HEIGHT }}>
        <div
          className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700"
          style={{ minHeight: HERO_MIN_HEIGHT }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/15 via-gray-800/40 to-transparent"></div>
          <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 md:gap-12 p-6 sm:p-8 md:p-12">
            <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-black">
              <img
                src={DEFAULT_SHOW_IMAGE}
                alt="Eruption Radio live show placeholder"
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover opacity-80"
                width={300}
                height={300}
                {...({ fetchpriority: 'high' } as Record<string, string>)}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/75" />
            </div>

            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start mb-4">
                <LoadingDots size="small" className="mr-3" />
                <span className="text-slate-200 text-sm sm:text-base font-medium tracking-wide">
                  CHECKING LIVE STATUS...
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
                Eruption Radio
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-slate-100 mb-4 font-medium">
                The Sound Of The Underground
              </p>
              <p className="text-slate-300 text-sm sm:text-base max-w-xl">
                Loading the current live show and schedule. You can still tune in while we fetch the latest on-air details.
              </p>

              <div className="flex justify-center sm:justify-start mt-6">
                <button
                  onClick={playLiveStream}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  ▶ Listen Live
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state (but still show offline message)
  if (error && !liveShow) {
    return (
      <div className={`w-full mb-8 sm:mb-12 ${className}`} style={{ minHeight: HERO_MIN_HEIGHT }}>
        <div
          className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700"
          style={{ minHeight: HERO_MIN_HEIGHT }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent"></div>
          
          {/* Content */}
          <div className="relative flex flex-col lg:flex-row items-center lg:items-start p-6 sm:p-8 md:p-12 gap-6 sm:gap-8 md:gap-12">
            {/* Left: Error Message */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start mb-4">
                <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                <span className="text-slate-200 text-sm sm:text-base font-medium">CURRENTLY OFFLINE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                No Live Show
              </h2>
              <p className="text-slate-200 text-sm sm:text-base mb-2">
                Check back soon for live broadcasts
              </p>
              <p className="text-red-400 text-xs">
                Schedule temporarily unavailable
              </p>
            </div>

            {/* Right: Contact Section - Desktop Only */}
            <div className="hidden lg:block w-80 flex-shrink-0">
              {/* Contact Text - Always Visible, Outside Border */}
              <div className="mb-4 text-center">
                <p className="text-white text-base font-semibold mb-1">
                  Text or WhatsApp the studio on
                </p>
                <p className="text-white text-2xl font-bold">
                  07927 281013
                </p>
              </div>
              
              {/* WhatsApp Widget - Conditionally Visible, Inside Border */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                <WhatsAppContact />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show offline state
  if (!liveShow) {
    return (
      <div className={`w-full mb-8 sm:mb-12 ${className}`} style={{ minHeight: HERO_MIN_HEIGHT }}>
        <div
          className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700"
          style={{ minHeight: HERO_MIN_HEIGHT }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent"></div>
          
          {/* Content */}
          <div className="relative flex flex-col lg:flex-row items-center lg:items-start p-6 sm:p-8 md:p-12 gap-6 sm:gap-8 md:gap-12">
            {/* Left: Offline Message */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start mb-4">
                <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                <span className="text-slate-200 text-sm sm:text-base font-medium">CURRENTLY OFFLINE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                No Live Show
              </h2>
              <p className="text-slate-200 text-sm sm:text-base">
                Check back soon for live broadcasts
              </p>
            </div>

            {/* Right: Contact Section - Desktop Only */}
            <div className="hidden lg:block w-80 flex-shrink-0">
              {/* Contact Text - Always Visible, Outside Border */}
              <div className="mb-4 text-center">
                <p className="text-white text-base font-semibold mb-1">
                  Text or WhatsApp the studio on
                </p>
                <p className="text-white text-2xl font-bold">
                  07927 281013
                </p>
              </div>
              
              {/* WhatsApp Widget - Conditionally Visible, Inside Border */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                <WhatsAppContact />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CRITICAL FIX: Don't return null if we have liveShow data but displayShow is temporarily null
  // This prevents the hero from disappearing during React Query updates
  if (!displayShow) {
    // If we have liveShow data from context, use it directly (fallback)
    if (liveShow) {
      debugVerbose('⚠️ LiveShowHero: displayShow is null but liveShow exists, using liveShow as fallback:', liveShow.show);
      // Use liveShow directly for rendering to prevent disappearing
      const fallbackShow = liveShow;
      
      const formatDateTime = (dateTimeString: string) => {
        const [date, time] = dateTimeString.split('#');
        const dateObj = new Date(`${date}T${time}`);
        
        return {
          date: dateObj.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
          }),
          time: dateObj.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          })
        };
      };

      const { date, time } = formatDateTime(fallbackShow.date_start_time);

      return (
        <div className={`w-full mb-8 sm:mb-12 ${className}`} style={{ minHeight: HERO_MIN_HEIGHT }}>
          <div
            className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700 hover:border-red-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/10"
            style={{ minHeight: HERO_MIN_HEIGHT }}
          >
            {/* Background pattern/texture */}
            <div className="absolute inset-0 bg-gradient-to-r from-gray-700/20 to-transparent"></div>
            
            <div className="relative p-6 sm:p-8 md:p-12">
              {/* Live status indicator */}
              <div className="flex items-center justify-center mb-6">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-3"></div>
                <span className="text-red-400 text-sm sm:text-base font-bold tracking-wider">LIVE NOW</span>
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse ml-3"></div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 md:gap-12">
                {/* Show image */}
                <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <OptimizedShowImage
                    show={fallbackShow}
                    size="medium"
                    className="w-full h-full"
                    imageClassName="opacity-75"
                    alt={fallbackShow.show}
                    priority={true}
                    preload
                    preconnect
                    preferredVariants={['400x400', '300x300']}
                    sizes="(max-width: 640px) 192px, (max-width: 768px) 256px, (max-width: 1024px) 288px, 320px"
                  />
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
                  </div>
                </div>

                {/* Show details */}
                <div className="flex-1 text-center sm:text-left">
                  {/* Show name */}
                  <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 leading-tight opacity-75">
                    {fallbackShow.show}
                  </h1>

                  {/* Sub-title */}
                  {renderSubTitleIfValid(fallbackShow.sub_title, (validSubTitle) => (
                    <h2 className="text-lg sm:text-xl md:text-xl lg:text-2xl text-white mb-4 sm:mb-6 font-medium leading-relaxed opacity-75">
                      {validSubTitle}
                    </h2>
                  ))}

                  {/* Presenter */}
                  <div className="mb-4 sm:mb-6">
                    <p className="text-slate-100 text-base sm:text-lg mb-2 opacity-75">
                      with <span className="font-semibold text-white">{fallbackShow.presenter.name}</span>
                    </p>
                    {fallbackShow.presenter.genres && (
                      <div className="hidden sm:flex flex-wrap justify-start gap-2">
                        {fallbackShow.presenter.genres.split(',').map((genre, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-red-500/20 text-red-300 text-sm rounded-full border border-red-500/30 opacity-75"
                          >
                            {genre.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Show time */}
                  <div className="mb-6 sm:mb-8">
                    <p className="text-slate-200 text-sm sm:text-base opacity-75">
                      {date} • {time}
                    </p>
                  </div>

                  {/* Play button */}
                  <button
                    onClick={playLiveStream}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl opacity-75 hover:opacity-100"
                  >
                    ▶ Listen Live
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // No live show and no displayShow - return null
    return (
      <div className={`w-full mb-8 sm:mb-12 ${className}`} style={{ minHeight: HERO_MIN_HEIGHT }}>
        <div
          className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700"
          style={{ minHeight: HERO_MIN_HEIGHT }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-700/20 to-transparent"></div>
          <div className="relative flex flex-col lg:flex-row items-center lg:items-start p-6 sm:p-8 md:p-12 gap-6 sm:gap-8 md:gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start mb-4">
                <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                <span className="text-slate-200 text-sm sm:text-base font-medium">CURRENTLY OFFLINE</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                No Live Show
              </h2>
                <p className="text-slate-200 text-sm sm:text-base">
                Check back soon for live broadcasts
              </p>
            </div>
            <div className="hidden lg:block w-80 flex-shrink-0">
              <div className="mb-4 text-center">
                <p className="text-white text-base font-semibold mb-1">
                  Text or WhatsApp the studio on
                </p>
                <p className="text-white text-2xl font-bold">
                  07927 281013
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                <WhatsAppContact />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDateTime = (dateTimeString: string) => {
    const [date, time] = dateTimeString.split('#');
    const dateObj = new Date(`${date}T${time}`);
    return {
      date: dateObj.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit',
        year: '2-digit'
      }),
      time: dateObj.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      })
    };
  };

  const { date, time } = formatDateTime(displayShow.date_start_time);
  const genres = displayShow.presenter.genres.split(', ');

      return (
        <div className={`w-full mb-8 sm:mb-12 ${className}`} style={{ minHeight: HERO_MIN_HEIGHT }}>
          <div
            className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-700 hover:border-red-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/10"
            style={{ minHeight: HERO_MIN_HEIGHT }}
          >
        {/* Background pattern/texture */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(239,68,68,0.1),transparent_50%)]"></div>
        
        {/* Content */}
        <div className="relative flex flex-col lg:flex-row items-center lg:items-start p-6 sm:p-8 md:p-10 gap-6 sm:gap-8 md:gap-10">
          {/* Left: Show Image and Details */}
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 md:gap-12 flex-1">
            {/* Show Image Section */}
            <div className="relative flex-shrink-0 flex flex-col items-center">
              <div className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl group-hover:scale-105 transition-transform duration-300">
                <div className={`h-full w-full transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                  <OptimizedShowImage
                    show={displayShow}
                    size="medium"
                    className="w-full h-full"
                    imageClassName="opacity-80"
                    alt={displayShow.show}
                    priority={true}
                    preload
                    preconnect
                    preferredVariants={['400x400', '300x300']}
                    sizes="(max-width: 640px) 192px, (max-width: 768px) 256px, (max-width: 1024px) 288px, 320px"
                  />
                </div>
              </div>
            </div>

            {/* Show Details */}
            <div className="flex-grow text-center sm:text-left min-w-0">
              {/* Live status header */}
              <div className="flex items-center justify-center sm:justify-start mb-3 sm:mb-4">
                <div className="relative w-3 h-3 mr-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="absolute inset-0 w-3 h-3 bg-red-500/50 rounded-full animate-pulse"></div>
                </div>
                <span className="text-rose-200 text-sm sm:text-base font-medium tracking-wide animate-pulse">LIVE NOW</span>
              </div>

              {/* Show name */}
              <h1 className={`text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 leading-tight transition-opacity duration-300 ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}>
                {displayShow.show}
              </h1>

              {/* Sub-title (replaces old show title) - with graceful null/empty handling */}
              {renderSubTitleIfValid(displayShow.sub_title, (validSubTitle) => (
                <h2 className={`text-lg sm:text-xl md:text-xl lg:text-2xl text-white mb-4 sm:mb-6 font-medium leading-relaxed transition-opacity duration-300 ${
                  isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}>
                  {validSubTitle}
                </h2>
              ))}

              {/* Genres (desktop only) */}
              <div
                className={`hidden sm:flex flex-wrap gap-2 justify-start mt-4 sm:mt-5 mb-4 sm:mb-6 transition-opacity duration-300 ${
                  isTransitioning ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {genres.map((genre, index) => (
                  <span
                    key={index}
                    className="bg-white/10 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full border border-white/20 hover:bg-white/20 transition-colors"
                  >
                    {genre.trim()}
                  </span>
                ))}
              </div>

              {/* Date and time */}
              <div className={`flex items-center justify-center sm:justify-start gap-4 text-slate-200 text-sm sm:text-base transition-opacity duration-300 ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{time}</span>
                </div>
              </div>

              {/* Listen now button - All screen sizes */}
              <div className="flex mt-6 sm:mt-8 flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={playLiveStream}
                  className="inline-flex items-center justify-center gap-3 bg-red-800 hover:bg-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-rose-200 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-full font-semibold text-sm sm:text-base transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  <span>Listen Live</span>
                </button>
              </div>
            </div>
          </div>

          {/* Right: Contact Section - Desktop Only */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            {/* Contact Text - Always Visible, Outside Border */}
            <div className="mb-4 text-center">
              <p className="text-white text-base font-semibold mb-1">
                Text or WhatsApp the studio on
              </p>
              <p className="text-white text-2xl font-bold">
                07927 281013
              </p>
            </div>
            
            {/* WhatsApp Widget - Conditionally Visible, Inside Border */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
              <WhatsAppContact />
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-4 right-4 w-20 h-20 bg-red-500/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-4 left-4 w-16 h-16 bg-red-500/5 rounded-full blur-lg"></div>
      </div>
    </div>
  );
};

/**
 * LiveShowHero with error boundary protection
 */
export const LiveShowHero: React.FC<LiveShowHeroProps> = (props) => {
  return (
    <StoreErrorBoundary>
      <LiveShowHeroContent {...props} />
    </StoreErrorBoundary>
  );
};
